import { ACCESS_LEVELS, type AccessLevel, type LocationType, type Role } from './constants';

export interface Actor {
  userId: string;
  role: Role;
  teamIds?: string[];
  /**
   * A limited member: a member who reaches only the places they have been given, like a guest,
   * while keeping a member's other abilities (creating work inside those places, seeing members).
   */
  limited?: boolean;
  /** Runs the whole product (the admin console flag): the one person who reaches personal spaces. */
  platformAdmin?: boolean;
}

/** Actions that depend only on the workspace role. */
export type WorkspaceAction =
  | 'workspace.view'
  | 'workspace.update'
  | 'workspace.delete'
  | 'workspace.billing'
  | 'members.view'
  | 'members.invite'
  | 'members.update'
  | 'members.remove'
  | 'teams.manage'
  | 'space.create'
  | 'content.create'
  | 'automation.manage'
  | 'import.run'
  | 'audit.view'
  | 'api.manage';

/** Actions on a resource, decided by the actor's resolved access level on it. */
export type ResourceAction = 'view' | 'comment' | 'edit' | 'manage' | 'delete' | 'share';

export type Action = WorkspaceAction | ResourceAction;

export interface Resource {
  type: string;
  /** Effective access level on the resource (see resolveAccess). null or undefined = no access. */
  access?: AccessLevel | null;
  /** Creator of the resource. Creators can always manage their own comments, views, clips and so on. */
  ownerId?: string | null;
}

const WORKSPACE_ACTIONS: Record<WorkspaceAction, Role[]> = {
  'workspace.view': ['owner', 'admin', 'member', 'guest'],
  'workspace.update': ['owner', 'admin'],
  'workspace.delete': ['owner'],
  'workspace.billing': ['owner'],
  'members.view': ['owner', 'admin', 'member'],
  'members.invite': ['owner', 'admin'],
  'members.update': ['owner', 'admin'],
  'members.remove': ['owner', 'admin'],
  'teams.manage': ['owner', 'admin'],
  'space.create': ['owner', 'admin', 'member'],
  'content.create': ['owner', 'admin', 'member'],
  'automation.manage': ['owner', 'admin', 'member'],
  'import.run': ['owner', 'admin'],
  'audit.view': ['owner', 'admin'],
  'api.manage': ['owner', 'admin', 'member'],
};

const REQUIRED_LEVEL: Record<ResourceAction, AccessLevel> = {
  view: 'view',
  comment: 'comment',
  edit: 'edit',
  manage: 'full',
  delete: 'full',
  share: 'full',
};

export function levelRank(level: AccessLevel | null | undefined): number {
  return level ? ACCESS_LEVELS.indexOf(level) + 1 : 0;
}

export function atLeast(level: AccessLevel | null | undefined, required: AccessLevel): boolean {
  return levelRank(level) >= levelRank(required);
}

export function maxLevel(a: AccessLevel | null, b: AccessLevel | null): AccessLevel | null {
  return levelRank(a) >= levelRank(b) ? a : b;
}

function isWorkspaceAction(action: Action): action is WorkspaceAction {
  return action in WORKSPACE_ACTIONS;
}

/**
 * Single permission check used by the server (enforcement) and the client (hiding UI).
 * Workspace actions depend on role. Resource actions depend on the resolved access level,
 * with the owner of a resource always allowed to act on it.
 */
export function can(actor: Actor | null | undefined, action: Action, resource?: Resource | null): boolean {
  if (!actor) return false;
  if (isWorkspaceAction(action)) {
    // A limited member works inside the places they were given, so no new spaces at the top level.
    if (action === 'space.create' && actor.limited) return false;
    return WORKSPACE_ACTIONS[action].includes(actor.role);
  }
  if (!resource) return false;
  if (resource.ownerId && resource.ownerId === actor.userId && resource.access !== null) return true;
  return atLeast(resource.access, REQUIRED_LEVEL[action]);
}

export interface LocationNode {
  id: string;
  type: LocationType;
  /** Folder: its space. List: its folder, or its space when it has no folder. Space: null. */
  parentId: string | null;
  isPrivate: boolean;
  /** A personal space: the one person it belongs to. Nobody else reaches it, admins included. */
  personalUserId?: string | null;
}

export interface Grant {
  resourceType: string;
  resourceId: string;
  principalType: 'user' | 'team';
  principalId: string;
  level: AccessLevel;
}

function grantFor(actor: Actor, node: LocationNode, grants: Grant[]): AccessLevel | null {
  let user: AccessLevel | null = null;
  let team: AccessLevel | null = null;
  for (const g of grants) {
    if (g.resourceType !== node.type || g.resourceId !== node.id) continue;
    if (g.principalType === 'user' && g.principalId === actor.userId) user = maxLevel(user, g.level);
    if (g.principalType === 'team' && actor.teamIds?.includes(g.principalId)) team = maxLevel(team, g.level);
  }
  // A grant made to the user directly wins over one inherited from a team.
  return user ?? team;
}

/**
 * Effective access level of an actor on a space, folder or list.
 *
 * Rules, in order:
 * 1. A personal space (and everything in it) belongs to one person: full for them and for a
 *    platform admin, nothing for anyone else, workspace owners, admins and grants included.
 * 2. Owners and admins have full access everywhere else.
 * 3. The nearest explicit grant (on the item, then its folder, then its space) wins.
 * 4. Guests and limited members see nothing without a grant.
 * 5. If the item or any ancestor is private, members without a grant see nothing.
 * 6. Otherwise members have full access.
 */
export function resolveAccess(
  actor: Actor,
  nodeId: string,
  nodes: ReadonlyMap<string, LocationNode>,
  grants: Grant[],
): AccessLevel | null {
  const chain: LocationNode[] = [];
  let current = nodes.get(nodeId);
  while (current && chain.length < 8) {
    chain.push(current);
    current = current.parentId ? nodes.get(current.parentId) : undefined;
  }
  if (chain.length === 0) return null;
  const personal = chain.find((n) => n.personalUserId);
  if (personal) return personal.personalUserId === actor.userId || actor.platformAdmin ? 'full' : null;
  if (actor.role === 'owner' || actor.role === 'admin') return 'full';
  for (const node of chain) {
    const level = grantFor(actor, node, grants);
    if (level) return level;
  }
  if (actor.role === 'guest' || actor.limited) return null;
  if (chain.some((n) => n.isPrivate)) return null;
  return 'full';
}

/**
 * Access to an item that lives under a location or at workspace level and can itself be private
 * (docs, dashboards, whiteboards, views). Private items are visible to their owner and to grantees.
 */
export function resolveItemAccess(
  actor: Actor,
  item: { type: string; id: string; isPrivate: boolean; ownerId: string | null },
  parentAccess: AccessLevel | null,
  grants: Grant[],
): AccessLevel | null {
  if (actor.role === 'owner' || actor.role === 'admin') return 'full';
  if (item.ownerId === actor.userId) return 'full';
  let granted: AccessLevel | null = null;
  for (const g of grants) {
    if (g.resourceType !== item.type || g.resourceId !== item.id) continue;
    if (g.principalType === 'user' && g.principalId === actor.userId) granted = maxLevel(granted, g.level);
    if (g.principalType === 'team' && actor.teamIds?.includes(g.principalId))
      granted = maxLevel(granted, g.level);
  }
  if (granted) return granted;
  if (item.isPrivate) return null;
  return parentAccess;
}
